import { useCallback, useEffect, useState } from 'react'
import { useFloorPlanStore } from '../store/useFloorPlanStore'

/**
 * Holds the (ephemeral, not persisted) set of selected table ids and wires
 * keyboard deletion. A page reload starts with nothing selected.
 *
 * Selection is a list so the marquee (drag-rectangle) can pick many tables at
 * once; clicking a single table replaces the selection, shift-click toggles.
 */
export function useFloorPlanSelection() {
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([])
  const deleteTable = useFloorPlanStore(s => s.deleteTable)
  const tables = useFloorPlanStore(s => s.tables)
  const activeRoomId = useFloorPlanStore(s => s.activeRoomId)

  // Only a table on the canvas stays selected. Switching rooms left the panel
  // editing a table in the room just left, out of sight, and an undo or a save
  // that adopts the backend's ids takes selected tables away the same way.
  useEffect(() => {
    setSelectedTableIds(prev => {
      const visible = prev.filter(id =>
        tables.some(table => table.id === id && table.roomId === activeRoomId)
      )
      return visible.length === prev.length ? prev : visible
    })
  }, [tables, activeRoomId])

  const select = useCallback((id: string, additive = false) => {
    setSelectedTableIds(prev => {
      if (additive) {
        return prev.includes(id)
          ? prev.filter(existing => existing !== id)
          : [...prev, id]
      }
      return [id]
    })
  }, [])

  const setSelection = useCallback(
    (ids: string[]) => setSelectedTableIds(ids),
    []
  )

  const clear = useCallback(() => setSelectedTableIds([]), [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return
      if (selectedTableIds.length === 0) return

      // Don't hijack deletion while the user is typing in a field.
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return
      }

      event.preventDefault()
      selectedTableIds.forEach(id => deleteTable(id))
      setSelectedTableIds([])
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedTableIds, deleteTable])

  return { selectedTableIds, select, setSelection, clear }
}
